/**
 * O que só uma rede pede no composer: TikTok (quem vê, interações, conteúdo
 * comercial) e YouTube (título, visibilidade, conteúdo infantil).
 *
 * TikTok: as diretrizes de integração exigem que o formulário mostre a conta
 * que vai publicar, as visibilidades que ELA permite (consultadas na hora),
 * nenhuma opção pré-marcada, a divulgação de conteúdo comercial e a
 * declaração de concordância com as políticas — sem isso o app não passa na
 * auditoria do TikTok.
 */

import { trpc } from "@/lib/trpc";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  TIKTOK_PRIVACY_LABELS,
  YOUTUBE_PRIVACY_LABELS,
  YOUTUBE_TITLE_MAX,
  type TikTokOptions,
  type TikTokPrivacy,
  type YouTubeOptions,
  type YouTubePrivacy,
} from "@shared/social-publishing";
import { IconeRede } from "./redes";

export function OpcoesTikTok({
  contaId,
  valor,
  onChange,
  varias = false,
}: {
  /** Primeira conta do TikTok escolhida: é a que o TikTok consulta. */
  contaId: number;
  valor: TikTokOptions;
  onChange: (v: TikTokOptions) => void;
  /** Mais de uma conta do TikTok escolhida: as opções valem para todas. */
  varias?: boolean;
}) {
  const criador = trpc.social.contas.tiktokCriador.useQuery(
    { accountId: contaId },
    { staleTime: 60_000, retry: false, meta: { erroNaTela: true } },
  );
  const c = criador.data;
  const set = (patch: Partial<TikTokOptions>) => onChange({ ...valor, ...patch });
  const comercial = valor.brandOrganic || valor.brandedContent;

  return (
    <section className="space-y-3 rounded border p-3">
      <div className="flex items-center gap-2">
        <IconeRede rede="tiktok" />
        <Label>TikTok</Label>
        {c && (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            {c.foto && <img src={c.foto} alt="" className="h-5 w-5 rounded-full object-cover" />}
            Publicando como <strong>{c.apelido ?? c.usuario}</strong>
          </span>
        )}
      </div>
      {criador.isLoading && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Consultando o que a conta permite…
        </p>
      )}
      {criador.error && <p className="text-xs text-red-600">{criador.error.message}</p>}
      {varias && (
        <p className="text-xs text-muted-foreground">
          As opções abaixo valem para todas as contas do TikTok escolhidas; a lista de visibilidades é a da primeira. Se
          outra conta não permitir a escolha, ela falha com o motivo e as demais saem normalmente.
        </p>
      )}

      <div className="space-y-1">
        <Label className="text-xs">Quem pode ver</Label>
        <Select value={valor.privacyLevel ?? ""} onValueChange={(v) => set({ privacyLevel: v as TikTokPrivacy })}>
          <SelectTrigger className="w-full sm:w-80">
            <SelectValue placeholder="Escolha a visibilidade" />
          </SelectTrigger>
          <SelectContent>
            {(c?.visibilidades.length ? c.visibilidades : Object.keys(TIKTOK_PRIVACY_LABELS)).map((p) => (
              <SelectItem key={p} value={p} disabled={p === "SELF_ONLY" && valor.brandedContent}>
                {TIKTOK_PRIVACY_LABELS[p as TikTokPrivacy] ?? p}
                {p === "SELF_ONLY" && valor.brandedContent ? " (não vale para conteúdo de marca)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        {(
          [
            ["allowComment", "Comentários", c?.comentarioDesligado],
            ["allowDuet", "Dueto", c?.duetoDesligado],
            ["allowStitch", "Costura (Stitch)", c?.stitchDesligado],
          ] as const
        ).map(([campo, rotulo, desligadoNaConta]) => (
          <label key={campo} className={`flex items-center gap-2 ${desligadoNaConta ? "opacity-50" : ""}`} title={desligadoNaConta ? "Desligado nas configurações da conta, no app do TikTok" : undefined}>
            <Checkbox
              checked={valor[campo] && !desligadoNaConta}
              disabled={!!desligadoNaConta}
              onCheckedChange={(v) => set({ [campo]: v === true } as Partial<TikTokOptions>)}
            />
            Permitir {rotulo.toLowerCase()}
          </label>
        ))}
      </div>

      <div className="space-y-2 rounded bg-muted/40 p-2">
        <label className="flex items-center justify-between gap-4 text-sm">
          Divulgar conteúdo comercial
          <Switch
            checked={comercial}
            onCheckedChange={(v) => set(v ? { brandOrganic: true } : { brandOrganic: false, brandedContent: false })}
          />
        </label>
        {comercial && (
          <div className="space-y-1.5 pl-1 text-sm">
            <label className="flex items-start gap-2">
              <Checkbox checked={valor.brandOrganic} onCheckedChange={(v) => set({ brandOrganic: v === true })} />
              <span>
                Sua marca <span className="text-xs text-muted-foreground">— promove o seu próprio negócio. O post leva o rótulo "Conteúdo promocional".</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox
                checked={valor.brandedContent}
                onCheckedChange={(v) =>
                  set({
                    brandedContent: v === true,
                    ...(v === true && valor.privacyLevel === "SELF_ONLY" ? { privacyLevel: null } : {}),
                  })
                }
              />
              <span>
                Conteúdo de marca <span className="text-xs text-muted-foreground">— parceria paga com outra marca. O post leva o rótulo "Parceria paga".</span>
              </span>
            </label>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={valor.aiGenerated} onCheckedChange={(v) => set({ aiGenerated: v === true })} />
        Conteúdo gerado por IA
      </label>

      <p className="text-xs text-muted-foreground">
        Ao publicar, você concorda com a{" "}
        <a className="underline" href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">
          Confirmação de Uso de Música
        </a>
        {valor.brandedContent && (
          <>
            {" "}e a{" "}
            <a className="underline" href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer">
              Política de Conteúdo de Marca
            </a>
          </>
        )}{" "}
        do TikTok. Depois de publicado, o TikTok pode levar alguns minutos processando até o vídeo aparecer no perfil.
        {c?.duracaoMaximaS ? ` Esta conta aceita vídeos de até ${Math.floor(c.duracaoMaximaS / 60)} min.` : ""}
      </p>
    </section>
  );
}

export function OpcoesYouTube({ valor, onChange }: { valor: YouTubeOptions; onChange: (v: YouTubeOptions) => void }) {
  const set = (patch: Partial<YouTubeOptions>) => onChange({ ...valor, ...patch });
  return (
    <section className="space-y-3 rounded border p-3">
      <div className="flex items-center gap-2">
        <IconeRede rede="youtube" />
        <Label>YouTube</Label>
      </div>
      <div className="space-y-1">
        <Label htmlFor="yt-titulo" className="text-xs">
          Título do vídeo
        </Label>
        <Input id="yt-titulo" value={valor.title} maxLength={YOUTUBE_TITLE_MAX} onChange={(e) => set({ title: e.target.value })} />
        <p className="text-xs text-muted-foreground">
          {valor.title.length}/{YOUTUBE_TITLE_MAX} · A legenda vira a descrição do vídeo. Vídeo vertical de até 3 min vira Short.
        </p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Visibilidade</Label>
        <Select value={valor.privacy} onValueChange={(v) => set({ privacy: v as YouTubePrivacy })}>
          <SelectTrigger className="w-full sm:w-80">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(YOUTUBE_PRIVACY_LABELS) as YouTubePrivacy[]).map((p) => (
              <SelectItem key={p} value={p}>
                {YOUTUBE_PRIVACY_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">O vídeo é feito para crianças?</Label>
        <RadioGroup
          value={valor.madeForKids == null ? "" : valor.madeForKids ? "sim" : "nao"}
          onValueChange={(v) => set({ madeForKids: v === "sim" })}
          className="flex gap-4"
        >
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="nao" /> Não
          </label>
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="sim" /> Sim, é para crianças
          </label>
        </RadioGroup>
        <p className="text-xs text-muted-foreground">Declaração obrigatória do YouTube. Vídeo para crianças perde comentários e alguns recursos.</p>
      </div>
    </section>
  );
}
